import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Vigil() {
  const { user } = useAuth();
  const [prayers, setPrayers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/vigil").then(({ data }) => setPrayers(data.prayers)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const hold = async (p) => {
    if (!user) { toast("Please sign in to hold a prayer."); return; }
    try {
      if (p.is_held) {
        await api.delete(`/me/hold/${p.id}`);
        toast("Quietly removed from your list.");
      } else {
        await api.post(`/me/hold/${p.id}`);
        toast("Matter being held.");
      }
      setPrayers((prev) => prev.map((x) => x.id === p.id ? { ...x, is_held: !x.is_held } : x));
    } catch {
      toast("Something went wrong.");
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-20">
      <div className="text-center mb-20 animate-fade-in">
        <p className="mono-label mb-6">The Vigil</p>
        <h1 className="font-heading text-4xl md:text-5xl text-ink leading-tight">A quiet time of attention</h1>
        <p className="font-body text-lg text-ink-secondary mt-6 max-w-xl mx-auto leading-relaxed">
          Read slowly. There is nothing to scroll toward. When you are ready, you may choose to hold a prayer.
        </p>
      </div>

      {loading ? (
        <p className="text-center mono-label">Attending…</p>
      ) : prayers.length === 0 ? (
        <p className="text-center font-body text-ink-tertiary italic py-16">The circle is quiet for now. Please return soon.</p>
      ) : (
        <div className="space-y-24">
          {prayers.map((p, i) => (
            <article key={p.id} className="animate-fade-up" style={{ animationDelay: `${i * 120}ms` }} data-testid={`vigil-prayer-${p.id}`}>
              <div className="flex items-center justify-between mb-5">
                <span className="mono-label">{p.category}{p.location ? ` · ${p.location}` : ""}</span>
                <span className="mono-label">No. {String(p.prayer_number || 0).padStart(6, "0")}</span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl text-ink">{p.display_name}</h2>
              <p className="font-body text-xl text-ink-secondary mt-5 leading-relaxed">{p.excerpt}</p>
              {p.opportunity_to_serve && p.practical_help && (
                <p className="font-body text-ink mt-4 border-l-2 border-sage pl-4 italic">An opportunity to be the answer: {p.practical_help}</p>
              )}
              <div className="mt-8 flex items-center gap-6">
                <button onClick={() => hold(p)} className={p.is_held ? "btn-solid" : "btn-ink"} data-testid={`hold-${p.id}`}>
                  {p.is_held ? "Being held by you" : "Hold this prayer"}
                </button>
                {p.holders_count > 0 && <span className="mono-label">Held by {p.holders_count}</span>}
              </div>
            </article>
          ))}
        </div>
      )}

      <div className="text-center mt-28 paper-rule pt-16">
        <p className="font-heading italic text-3xl md:text-4xl text-ink leading-snug">May these people be held in love.</p>
        <p className="font-heading italic text-2xl md:text-3xl text-terracotta mt-8 leading-snug">
          May my life today become an answer to someone's prayer.
        </p>
        {!user && (
          <Link to="/register" className="btn-ink mt-12 inline-block" data-testid="vigil-join">Join the circle to hold prayers</Link>
        )}
      </div>
    </div>
  );
}
